import { useContext, useState } from "react";
import {
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { IProject } from "interfaces/project";
import { Dialog } from "components/Dialog";
import { EditProjectDialog } from "pages/Projects/ProjectDialogs/EditProjectDialog";
import { formatID } from "core/formatID";
import { API_URL_PROJECTS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { useProjectsQuery } from "hooks/queries/useProjectsQuery";
import { ProjectPercentIndicatorCircle } from "components/PercentIndicatorCircle/ProjectPercentIndicatorCircle";

interface Props {
  selectedProject: IProject;
}

type AnchorElType = (EventTarget & HTMLButtonElement) | null;

export const ProjectsHeader = ({ selectedProject }: Props): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const history = useHistory();
  const { data: projects } = useProjectsQuery();

  const [anchorEl, setAnchorEl] = useState<AnchorElType>(null);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const queryClient = useQueryClient();

  const deleteProjectQuery = useMutation(["delete-project"], {
    mutationFn: async (projectId: number) => {
      const response = await axios.delete(`${API_URL_PROJECTS}/${projectId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projects"]
      });

      addSnackbarToQueue({
        message: "Project deleted successfully.",
        severity: "success"
      });

      setDeleteDialogVisible(false);

      const topProjectId = projects?.results[0].id ?? "";
      history.replace(`/pages/projects/${topProjectId}`);
    },
    onError: () => {
      addSnackbarToQueue({
        message: "Error deleting Project.",
        severity: "error"
      });
    }
  });

  return (
    <>
      <Stack>
        <Stack direction="row" spacing={4} alignItems="center">
          <Tooltip title={selectedProject?.name} placement="bottom-start">
            <Typography
              variant="h5"
              component="h1"
              flexShrink={1}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
                whiteSpace: "wrap",
                width: "calc(100% - 150px)"
              }}
            >
              {selectedProject?.name}
            </Typography>
          </Tooltip>
          <Stack
            justifyContent="space-between"
            direction="row"
            flexGrow={1}
            alignItems="center"
            spacing={2}
          >
            <ProjectPercentIndicatorCircle
              // prettier-ignore
              actionStates={{
                  open_action_count: selectedProject.open_action_count,
                  in_progress_action_count: selectedProject.in_progress_action_count,
                  in_review_action_count: selectedProject.in_review_action_count,
                  completed_action_count: selectedProject.completed_action_count,
                  canceled_action_count: selectedProject.canceled_action_count
                }}
              size={40}
              strokeWidth={4}
            />
            <Chip label={selectedProject?.project_status} size="small" />

            <Tooltip title="More Options" placement="top">
              <IconButton
                aria-label="More Options"
                onClick={event => setAnchorEl(event.currentTarget)}
                size="large"
              >
                <MoreHoriz />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={() => setEditDialogVisible(true)}>
                Edit
              </MenuItem>
              <MenuItem onClick={() => setDeleteDialogVisible(true)}>
                Delete
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        <Typography variant="h6" component="p">
          {formatID(selectedProject?.id, "P")}
        </Typography>
      </Stack>

      <EditProjectDialog
        isOpen={editDialogVisible}
        onClose={() => setEditDialogVisible(false)}
        selectedProject={selectedProject}
        onConfirm={async _updateProject => {
          setAnchorEl(null);
          setEditDialogVisible(false);
          await queryClient.invalidateQueries({ queryKey: ["projects"] });
        }}
      />

      <Dialog
        dialogId="dialog-delete-project"
        open={deleteDialogVisible}
        title="Delete Project"
        confirmText="Confirm"
        size="medium"
        confirmDisabled={selectedProject?.cases.length > 0}
        onSubmit={() => {
          deleteProjectQuery.mutate(selectedProject?.id);
        }}
        onClose={() => setDeleteDialogVisible(false)}
        aria-label="delete-project-form"
        data-qa="PP-Delete-Project"
        data-testid="delete-project-dialog"
        allowClickaway={true}
      >
        {selectedProject?.cases.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You have cases linked to the project.
            </Alert>

            <Typography>
              Please remove all cases from this project before trying to delete.
            </Typography>
          </>
        )}

        {selectedProject?.cases.length === 0 && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action cannot be undone.
            </Alert>

            <Typography>
              Are you sure you want to delete the project?
            </Typography>
          </>
        )}
      </Dialog>
    </>
  );
};
